import styled from "styled-components";
import React from "react";
import {Observer} from "mobx-react";
import {baseDistance, ELLIPSIS, FlexCenter, VerticalFlex, VerticalFlexStyle} from "../../styles/styles";
import {useAppStore} from "../../../../StateManager";
import {FieldDataType, XyiconFeature} from "../../../../generated/api/base";
import {StringUtils} from "../../../../utils/data/string/StringUtils";
import type {ListObject} from "../../../../data/models/field/datatypes/SingleSelect";
import {BasicColors} from "../../../../utils/BasicColors";
import {ArrayUtils} from "../../../../utils/data/array/ArrayUtils";
import {TitleStyled} from "./PortfolioList";
import type {IPieChartWidgetData} from "./HomeDashboard";

interface IPieChartProps {
	readonly configuration: IPieChartWidgetData;
}

export const PieChartWidget = (props: IPieChartProps) => {
	const {configuration} = props;
	const appState = useAppStore((state) => state.appState);

	return (
		<Observer>
			{() => {
				const field = appState.actions.getFieldByRefId(props.configuration.targetFieldRefId);

				if (!field || field.dataType !== FieldDataType.SingleChoiceList) {
					return (
						<PieChartWidgetStyled>
							<TitleStyled $color={configuration.title.color}>{configuration.title.text}</TitleStyled>
							<div>There is no data to display.</div>
						</PieChartWidgetStyled>
					);
				}

				// getting the list of values and its colors
				const choiceList = [...((field.dataTypeSettings.choiceList as ListObject[]) || [])].map((val) => ({value: val.value, color: val.color}));

				// getting a filtered array of a shuffled, basic colors, which are not presented in the choicelist
				const basicColors = ArrayUtils.shuffle(BasicColors.getBasicColorsV5()).filter(
					(color) => !choiceList.find((choice) => choice.color === color.hex),
				);

				// getting the field datas, to get the frequency of the values
				const fieldDatas: (string | null)[] = appState.actions
					.getList(XyiconFeature.Portfolio)
					.filter((p) => appState.actions.isFieldAssignedToItem(field, p))
					.map((p) => p.fieldData?.[configuration.targetFieldRefId] || null);
				const map = fieldDatas.reduce((acc, label) => acc.set(label, (acc.get(label) || 0) + 1), new Map());

				// build the actual dataset which can be used by the chart
				const testDataset = [...map.entries()]
					.map((entry) => ({
						color: `#${choiceList.find((val) => StringUtils.equalsIgnoreCase(val.value, entry[0]))?.color || (basicColors.length > 0 ? basicColors.pop()?.hex : "000")}`,
						label: entry[0],
						value: entry[1],
					}))
					.sort((a, b) => StringUtils.sortIgnoreCase(a.label, b.label));

				// math for rendering out the piechart
				let conicGradient = "";
				const arr: number[] = [];
				const sum = testDataset.reduce((acc, currentValue) => acc + currentValue.value, 0);

				const getPercentage = (value: number) => {
					return Math.round(Math.round((value / sum) * 100));
				};

				for (let j = 0; j < testDataset.length; j++) {
					if (j === 0) {
						arr.push(getPercentage(testDataset[j].value));
					} else {
						arr.push(getPercentage(testDataset[j].value) + arr[j - 1]);
					}
				}

				const overlap = 0.25;
				for (let i = 0; i < arr.length; i++) {
					conicGradient += `${testDataset[i].color} `;

					if (i === 0) {
						if (arr.length === 1) {
							conicGradient += `${arr[i]}%`;
						} else {
							conicGradient += `${arr[i]}%, `;
						}
					} else if (i === testDataset.length - 1) {
						conicGradient += `${arr[i - 1]}% `;
					} else {
						conicGradient += `${arr[i - 1] + overlap}% ${arr[i] - overlap}%,`;
					}
				}

				const style: React.CSSProperties = {
					background: `conic-gradient(${conicGradient})`,
				};

				return (
					<PieChartWidgetStyled>
						<TitleStyled $color={configuration.title.color}>{configuration.title.text}</TitleStyled>
						<PieChartBodyStyled
							$gap={baseDistance.lg}
							$flex="1"
							style={{justifyContent: "center"}}
						>
							{testDataset.length > 0 ? (
								<>
									<Pie style={style} />
									<OptionsList $gap={baseDistance.md}>
										{testDataset.map((item) => (
											<FlexCenter
												$gap={baseDistance.sm}
												key={item.color}
												className="option"
											>
												<div style={{width: 32, height: 16, backgroundColor: item.color}} />
												<span title={item.label || "Blanks"}>
													{item.label || "Blanks" /* SingleSelect values cannot be empty, so only the blanks will get the "Blanks" label */}
												</span>
											</FlexCenter>
										))}
									</OptionsList>
								</>
							) : (
								<div>No data available.</div>
							)}
						</PieChartBodyStyled>
					</PieChartWidgetStyled>
				);
			}}
		</Observer>
	);
};

const PieChartWidgetStyled = styled.div`
	padding: ${baseDistance.md};
	${VerticalFlexStyle};
	flex: 1;
	gap: ${baseDistance.md};
	height: 100%;
	width: 100%;
`;

const OptionsList = styled(VerticalFlex)`
	max-height: calc(100% - 16px);
	overflow: auto;

	.option {
		div {
			min-width: 32px;
		}

		span {
			${ELLIPSIS}
		}
	}
`;

const Pie = styled.div`
	border-radius: 50%;
	aspect-ratio: 1 / 1;
	width: 50%;
`;

const PieChartBodyStyled = styled(FlexCenter)`
	max-height: calc(100% - 48px);

	${Pie}, ${OptionsList} {
		width: calc(50% - 16px);
	}
`;
